import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'

import type { ApiClient } from '@/api/client'
import { useApiClient } from '@/api/ApiClientProvider'
import type { DigestPeriod, DigestQuery } from '@/api/types'

/**
 * The insights reads the Insights pages and the Overview's cards share: one
 * cache, so every surface that shows or counts an insight agrees.
 */

/**
 * A tenant-wide Insights read that no global filter narrows (alerts, trends):
 * keyed without the filters, so changing them neither refetches nor splits
 * the cache, and the Overview and the Insights pages share one answer.
 */
export function useInsightsRead<T>(name: string, fetch: (client: ApiClient) => Promise<T>) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['insights', name, client.kind],
    queryFn: () => fetch(client),
  })
}

/** Everything that shows an insight or counts one: the Insights pages and the Overview's cards. */
export function invalidateInsights(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['insights'] }),
    queryClient.invalidateQueries({ queryKey: ['overview'] }),
  ])
}

/** Which digest a cached query holds: named fields, so nothing reads the key by position. */
interface DigestKeyParams {
  period: DigestPeriod
  version: number | 'latest'
}

/**
 * The digest's own query key: a digest is tenant-wide, so the global filters
 * are not part of it. `period` and `version` sit in one object, which a prefix
 * key (`{period}` alone) still matches.
 */
function digestKey(kind: ApiClient['kind'], params: Partial<DigestKeyParams>) {
  return ['insights', 'digest', kind, params] as const
}

function digestKeyParams(query: DigestQuery): DigestKeyParams {
  return { period: query.period, version: query.version ?? 'latest' }
}

/** The params of a key `digestKey` built; null for any other key. */
function readDigestKey(key: QueryKey | undefined): DigestKeyParams | null {
  const params = key?.[3]
  if (key?.[1] !== 'digest' || typeof params !== 'object' || params === null) return null
  const { period, version } = params as Partial<DigestKeyParams>
  return period !== undefined && version !== undefined ? { period, version } : null
}

/**
 * One period's digest: the latest, or the version named. Shared by the digest
 * page and the Overview's summary card. While another version of the same
 * period loads, the one on screen stays; another period's digest never stands
 * in for this one (its dates, writer and label would be wrong).
 */
export function useDigestQuery(query: DigestQuery) {
  const client = useApiClient()
  return useQuery({
    queryKey: digestKey(client.kind, digestKeyParams(query)),
    queryFn: () => client.digest(query),
    placeholderData: (previous, previousQuery) =>
      readDigestKey(previousQuery?.queryKey)?.period === query.period ? previous : undefined,
  })
}

/** Writes a new version of a period's digest; the answer becomes that period's latest. */
export function useRegenerateDigest() {
  const client = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (period: DigestPeriod) => client.regenerateDigest(period),
    onSuccess: (report, period) => {
      // The answer is the period's new latest; its named versions now list one more, so they are stale.
      queryClient.setQueryData(digestKey(client.kind, digestKeyParams({ period })), report)
      return queryClient.invalidateQueries({
        queryKey: digestKey(client.kind, { period }),
        predicate: (cached) => readDigestKey(cached.queryKey)?.version !== 'latest',
      })
    },
  })
}
